import type {
  WorkflowBlock, Connector, BlockGroup, WorkflowState,
  CrossSheetConnector,
} from '@/types/workflow';

/**
 * A minimal operation-based diff between two workflow states.
 *
 * Sending this instead of full snapshots over the realtime channel preserves
 * local in-progress edits: receiving a peer delta patches the entities they
 * touched without clobbering unrelated entities the local user is currently
 * dragging / typing / connecting.
 *
 * This is CRDT-lite, not a real CRDT — simultaneous writes to the **same**
 * field still last-write-wins (by arrival order on the channel). It is, however,
 * a large improvement over the previous full-state replace, which clobbered
 * every local edit on every peer push.
 */
export interface WorkflowDelta {
  blocksAdded: WorkflowBlock[];
  blocksUpdated: Array<{ id: string; patch: Partial<WorkflowBlock> }>;
  blocksDeleted: string[];
  connectorsAdded: Connector[];
  connectorsUpdated: Array<{ id: string; patch: Partial<Connector> }>;
  connectorsDeleted: string[];
  groupsAdded: BlockGroup[];
  groupsUpdated: Array<{ id: string; patch: Partial<BlockGroup> }>;
  groupsDeleted: string[];
  /**
   * Cross-sheet-connectors. Optional så delta-payloads från single-
   * sheet-workflows bakåtkompatibla är ofattliga (undefined = ingen
   * cross-sheet-mutation).
   */
  crossSheetAdded?: CrossSheetConnector[];
  crossSheetUpdated?: Array<{ id: string; patch: Partial<CrossSheetConnector> }>;
  crossSheetDeleted?: string[];
  /** Workflow-level fields. Only included when changed. */
  meta?: { name?: string };
}

export function emptyDelta(): WorkflowDelta {
  return {
    blocksAdded: [], blocksUpdated: [], blocksDeleted: [],
    connectorsAdded: [], connectorsUpdated: [], connectorsDeleted: [],
    groupsAdded: [], groupsUpdated: [], groupsDeleted: [],
  };
}

export function isDeltaEmpty(d: WorkflowDelta): boolean {
  const cs =
    (d.crossSheetAdded?.length ?? 0) === 0 &&
    (d.crossSheetUpdated?.length ?? 0) === 0 &&
    (d.crossSheetDeleted?.length ?? 0) === 0;
  return (
    d.blocksAdded.length === 0 && d.blocksUpdated.length === 0 && d.blocksDeleted.length === 0 &&
    d.connectorsAdded.length === 0 && d.connectorsUpdated.length === 0 && d.connectorsDeleted.length === 0 &&
    d.groupsAdded.length === 0 && d.groupsUpdated.length === 0 && d.groupsDeleted.length === 0 &&
    cs && !d.meta
  );
}

function shallowDiff<T extends object>(a: T, b: T): Partial<T> | null {
  const patch: Partial<T> = {};
  const keys = new Set<keyof T>([...(Object.keys(a) as (keyof T)[]), ...(Object.keys(b) as (keyof T)[])]);
  let changed = false;
  for (const key of keys) {
    const av = a[key];
    const bv = b[key];
    if (!deepEqual(av, bv)) {
      patch[key] = bv;
      changed = true;
    }
  }
  return changed ? patch : null;
}

function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a === null || b === null || typeof a !== typeof b) return false;
  if (typeof a !== 'object') return false;
  if (Array.isArray(a)) {
    if (!Array.isArray(b) || a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) if (!deepEqual(a[i], b[i])) return false;
    return true;
  }
  const ao = a as Record<string, unknown>;
  const bo = b as Record<string, unknown>;
  const keys = new Set([...Object.keys(ao), ...Object.keys(bo)]);
  for (const k of keys) if (!deepEqual(ao[k], bo[k])) return false;
  return true;
}

function diffCollection<T extends { id: string }>(
  prev: T[],
  curr: T[],
): { added: T[]; updated: Array<{ id: string; patch: Partial<T> }>; deleted: string[] } {
  const prevMap = new Map(prev.map(e => [e.id, e]));
  const currMap = new Map(curr.map(e => [e.id, e]));
  const added: T[] = [];
  const updated: Array<{ id: string; patch: Partial<T> }> = [];
  const deleted: string[] = [];

  for (const [id, c] of currMap) {
    const p = prevMap.get(id);
    if (!p) { added.push(c); continue; }
    const patch = shallowDiff(p, c);
    if (patch) updated.push({ id, patch });
  }
  for (const id of prevMap.keys()) if (!currMap.has(id)) deleted.push(id);
  return { added, updated, deleted };
}

/**
 * Compute the delta from `prev` → `curr`. Empty delta if nothing changed.
 */
export function computeDelta(prev: WorkflowState, curr: WorkflowState): WorkflowDelta {
  const blocks = diffCollection(prev.blocks, curr.blocks);
  const connectors = diffCollection(prev.connectors, curr.connectors);
  const groups = diffCollection(prev.groups, curr.groups);
  const meta = prev.name !== curr.name ? { name: curr.name } : undefined;
  return {
    blocksAdded: blocks.added,
    blocksUpdated: blocks.updated,
    blocksDeleted: blocks.deleted,
    connectorsAdded: connectors.added,
    connectorsUpdated: connectors.updated,
    connectorsDeleted: connectors.deleted,
    groupsAdded: groups.added,
    groupsUpdated: groups.updated,
    groupsDeleted: groups.deleted,
    meta,
  };
}

/**
 * Return true when the local entity was mutated AFTER the incoming patch.
 * In that case the peer's update is stale and should be skipped — this is
 * per-entity last-write-wins, the minimum viable conflict-resolution
 * strategy without a full CRDT.
 *
 * Absent mtime on either side defaults to 0, so legacy entities never
 * "win" against a mtime-tracked peer update (and vice-versa: a fresh
 * peer update with no mtime won't clobber a mtime-tracked local change
 * either).
 */
function isStalePatch(local: { mtime?: number }, patch: { mtime?: number }): boolean {
  const localTime = local.mtime ?? 0;
  const patchTime = patch.mtime ?? 0;
  // Strict greater-than: equal timestamps (nearly-simultaneous edits) fall
  // through to the standard merge so neither client silently drops changes.
  return localTime > patchTime;
}

/**
 * Apply `delta` to `state`, returning a new state with the peer's changes
 * merged into whatever the local user has in flight. Entities the delta
 * doesn't mention are preserved untouched. For updates, per-entity
 * last-write-wins via mtime — if the local copy was modified after the
 * patch was produced, the patch is skipped.
 */
export function applyDelta(state: WorkflowState, delta: WorkflowDelta): WorkflowState {
  const blockById = new Map(state.blocks.map(b => [b.id, b]));
  for (const add of delta.blocksAdded) blockById.set(add.id, add);
  for (const { id, patch } of delta.blocksUpdated) {
    const b = blockById.get(id);
    if (!b) continue;
    if (isStalePatch(b, patch)) continue;
    blockById.set(id, { ...b, ...patch });
  }
  for (const id of delta.blocksDeleted) blockById.delete(id);

  const connById = new Map(state.connectors.map(c => [c.id, c]));
  for (const add of delta.connectorsAdded) connById.set(add.id, add);
  for (const { id, patch } of delta.connectorsUpdated) {
    const c = connById.get(id);
    if (!c) continue;
    if (isStalePatch(c, patch)) continue;
    connById.set(id, { ...c, ...patch });
  }
  for (const id of delta.connectorsDeleted) connById.delete(id);

  const groupById = new Map(state.groups.map(g => [g.id, g]));
  for (const add of delta.groupsAdded) groupById.set(add.id, add);
  for (const { id, patch } of delta.groupsUpdated) {
    const g = groupById.get(id);
    if (!g) continue;
    if (isStalePatch(g, patch)) continue;
    groupById.set(id, { ...g, ...patch });
  }
  for (const id of delta.groupsDeleted) groupById.delete(id);

  return {
    ...state,
    name: delta.meta?.name ?? state.name,
    blocks: Array.from(blockById.values()),
    connectors: Array.from(connById.values()),
    groups: Array.from(groupById.values()),
  };
}

// ─── Cross-sheet delta helpers (multi-sheet-aware) ────────────────
//
// `CrossSheetConnector` lever på multi-sheet-state-roten, inte i en
// enskild sheet. applyDelta(WorkflowState, delta) ignorerar dessa —
// multi-sheet-callers använder `computeCrossSheetDelta` +
// `applyCrossSheetDelta` separat mot sin `crossSheetConnectors`-
// array.

/**
 * Producera delta för cross-sheet-connector-arrays. Resultatet är en
 * partial WorkflowDelta som kan merge:as in i en huvud-delta innan
 * broadcast.
 */
export function computeCrossSheetDelta(
  prev: CrossSheetConnector[],
  curr: CrossSheetConnector[],
): Pick<WorkflowDelta, 'crossSheetAdded' | 'crossSheetUpdated' | 'crossSheetDeleted'> {
  const diff = diffCollection(prev, curr);
  return {
    crossSheetAdded: diff.added,
    crossSheetUpdated: diff.updated,
    crossSheetDeleted: diff.deleted,
  };
}

/**
 * Merge-in cross-sheet-connector-patches. Last-write-wins saknas
 * här (CrossSheetConnector har ingen `mtime`-fält idag) — per-
 * entity replace-merge används istället, vilket matchar befintligt
 * beteende.
 */
export function applyCrossSheetDelta(
  current: CrossSheetConnector[],
  delta: Pick<WorkflowDelta, 'crossSheetAdded' | 'crossSheetUpdated' | 'crossSheetDeleted'>,
): CrossSheetConnector[] {
  const byId = new Map(current.map(c => [c.id, c]));
  for (const add of delta.crossSheetAdded ?? []) byId.set(add.id, add);
  for (const { id, patch } of delta.crossSheetUpdated ?? []) {
    const c = byId.get(id);
    if (!c) continue;
    byId.set(id, { ...c, ...patch });
  }
  for (const id of delta.crossSheetDeleted ?? []) byId.delete(id);
  return Array.from(byId.values());
}
